/**
 * Handing a built filing across to Karr Copilot.
 *
 * ## Why there is no edge function here
 *
 * The spec first imagined Karr serving the payload from `roc-eform-data` behind
 * a token. Building it made that wrong on the repo's own terms: the payload is
 * computed by the pure builders in `src/shared/roc/eform/`, and standing
 * practice in this codebase is that **the deterministic brain does not get a
 * second copy inside an edge function** — the GST cockpit made the same call
 * for the same reason. An edge function could only either re-implement the
 * builders (a second brain that will drift) or call back into the app (which
 * has already computed it). So the app computes once, and hands the result
 * over directly.
 *
 * That also deletes the token, the token table, the expiry sweep and the
 * migration that would have carried them.
 *
 * ## How the browser allows it
 *
 * The extension declares `externally_connectable` for `karr.pro` and drops a
 * marker on the page — `<html data-karr-copilot="<its own runtime id>">`. So:
 *
 * - the id is never hard-coded here, which matters because an unpacked
 *   extension gets a different id on every machine it is loaded on;
 * - "is it installed?" is answerable *before* offering the button, rather than
 *   by firing a message into the dark and reading `lastError`;
 * - nothing crosses the network at all. The data goes from one tab to an
 *   extension on the same machine.
 *
 * The clipboard remains the fallback for a machine where the extension is not
 * installed, or where the user would rather paste it themselves.
 */

/** The marker the Copilot's content script writes on a Karr page. */
const MARKER = 'karrCopilot';

interface ChromeLike {
    runtime?: {
        sendMessage?: (
            extensionId: string,
            message: unknown,
            callback: (response?: { ok?: boolean; error?: string }) => void,
        ) => void;
        lastError?: { message?: string };
    };
}

const chromeApi = (): ChromeLike | undefined =>
    (globalThis as unknown as { chrome?: ChromeLike }).chrome;

/**
 * The extension's id, if it has announced itself on this page.
 *
 * `undefined` means "not installed" — or, just as honestly, "installed but not
 * permitted to talk to this origin", which from the app's side is the same
 * thing and leads to the same fallback.
 */
export function copilotExtensionId(): string | undefined {
    if (typeof document === 'undefined') return undefined;
    const id = document.documentElement.dataset[MARKER];
    return id && id.length > 0 ? id : undefined;
}

export type HandoffResult =
    | { status: 'sent' }
    | { status: 'not-installed' }
    | { status: 'refused'; reason: string };

/**
 * Send one built filing to the extension.
 *
 * Never throws: every failure is a value the caller can put on the screen,
 * because "it silently did nothing" is the failure mode this whole lane exists
 * to stamp out.
 */
export async function sendToExtension(payload: unknown): Promise<HandoffResult> {
    const id = copilotExtensionId();
    if (!id) return { status: 'not-installed' };
    const api = chromeApi();
    if (!api?.runtime?.sendMessage) return { status: 'not-installed' };

    return new Promise<HandoffResult>((resolve) => {
        // If the extension is uninstalled between the marker being written and
        // this call, the callback fires with lastError set and no response.
        const timer = setTimeout(
            () => resolve({ status: 'refused', reason: 'The extension did not answer. Reload the MCA tab and try again.' }),
            4000,
        );
        try {
            api.runtime!.sendMessage!(id, { type: 'karr.roc.eform/1', payload }, (response) => {
                clearTimeout(timer);
                const err = api.runtime?.lastError?.message;
                if (err) return resolve({ status: 'refused', reason: err });
                if (response?.ok === false) {
                    return resolve({ status: 'refused', reason: response.error || 'The extension rejected the filing data.' });
                }
                resolve({ status: 'sent' });
            });
        } catch (e) {
            clearTimeout(timer);
            resolve({ status: 'refused', reason: e instanceof Error ? e.message : 'Could not reach the extension.' });
        }
    });
}

export type IdentifyResult =
    | { status: 'identified'; installKey: string; version: string }
    | { status: 'not-installed' }
    | { status: 'refused'; reason: string };

/**
 * Ask the installed Copilot who it is (kcl-2).
 *
 * The reply is the INSTALL KEY: 32 random bytes the extension made on this
 * machine and keeps in Chrome's own storage. Karr binds a licence to its hash,
 * which is how one laptop is told from another — copying the extension folder
 * does not copy Chrome's storage. An older extension (before 2.1.0) does not
 * answer this message at all, and that comes back as `refused` naming the
 * upgrade rather than as a silent nothing.
 */
export async function identifyExtension(): Promise<IdentifyResult> {
    const id = copilotExtensionId();
    if (!id) return { status: 'not-installed' };
    const api = chromeApi();
    if (!api?.runtime?.sendMessage) return { status: 'not-installed' };

    return new Promise<IdentifyResult>((resolve) => {
        const timer = setTimeout(
            () => resolve({ status: 'refused', reason: 'The extension did not answer. It may be older than 2.1.0 — download the current Karr Copilot from Settings.' }),
            4000,
        );
        try {
            api.runtime!.sendMessage!(id, { type: 'karr.copilot.identify/1' }, (response) => {
                clearTimeout(timer);
                const err = api.runtime?.lastError?.message;
                if (err) return resolve({ status: 'refused', reason: err });
                const r = response as { ok?: boolean; installKey?: string; version?: string } | undefined;
                if (!r?.ok || !r.installKey) {
                    return resolve({ status: 'refused', reason: 'This Karr Copilot is older than 2.1.0 and cannot be licensed. Download the current one from Settings.' });
                }
                resolve({ status: 'identified', installKey: r.installKey, version: r.version ?? '' });
            });
        } catch (e) {
            clearTimeout(timer);
            resolve({ status: 'refused', reason: e instanceof Error ? e.message : 'Could not reach the extension.' });
        }
    });
}

export type ReloadResult = 'reloading' | 'unsupported' | 'unreachable';

/**
 * Ask the Copilot to restart from its folder (copselfupd, 2.9.0 on).
 *
 * Karr has just written the new version into that folder; this makes Chrome
 * read it. A Copilot older than 2.9.0 does not know the message and answers
 * "Unrecognised message", which comes back as `unsupported`: the person
 * presses the reload arrow on chrome://extensions once, and never again.
 */
export async function reloadExtension(): Promise<ReloadResult> {
    const id = copilotExtensionId();
    const api = chromeApi();
    if (!id || !api?.runtime?.sendMessage) return 'unreachable';

    return new Promise<ReloadResult>((resolve) => {
        const timer = setTimeout(() => resolve('unreachable'), 4000);
        try {
            api.runtime!.sendMessage!(id, { type: 'karr.copilot.reload/1' }, (response) => {
                clearTimeout(timer);
                if (api.runtime?.lastError?.message) return resolve('unreachable');
                resolve(response?.ok ? 'reloading' : 'unsupported');
            });
        } catch {
            clearTimeout(timer);
            resolve('unreachable');
        }
    });
}
